import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createTrackerReferenceResolver,
  purgeAllBrowserTrackerData,
  purgeBrowserTrackerOrganization,
  type TrackerIdentity,
  type TrackerReferenceResolver,
} from '@nimbalyst/collab-bundle/trackers-ui';
import type { TeamMember } from '../api';
import { TeamSessionError } from '../auth/teamJwt';
import { getOrgRoster, loadOrgRoster, memberDisplayName } from '../collab/orgRoster';
import { consoleConfig } from '../config';
import { getProjectRouteId } from '../projectSelection';
import { orgPageItemPath } from '../routing';
import type { OrganizationRouteContext } from '../routes/workspaceContext';
import { trackerIdentityForMember, trackerPresenceIdentity } from './trackerIdentity';
import { useBrowserTrackerSession, type BrowserTrackerSession } from './useBrowserTrackerSession';

/**
 * The org roster, which the tracker room needs before it can open.
 *
 * Unlike the docs surface -- where the roster only decides whether a comment
 * composer is offered -- here it decides *who the reader is*: the identity every
 * assignment-based view matches against, and the name teammates see in presence.
 * A room opened with a placeholder identity would answer "assigned to me" with
 * silence, so the session waits for this rather than starting without it.
 */
function useTrackerRoster(orgId: string, getTeamJwt: OrganizationRouteContext['getTeamJwt'], enabled: boolean) {
  const [members, setMembers] = useState<readonly TeamMember[]>(() => getOrgRoster(orgId));
  const [failed, setFailed] = useState(false);
  const [terminalFailure, setTerminalFailure] = useState<TeamSessionError['kind'] | null>(null);
  // Whether the roster is an answer yet. Without it, "the request is still in
  // flight" and "the request came back and you are not on it" are the same
  // empty identity, and the second one renders as the first: a spinner that
  // never resolves for someone who is simply not a member.
  const [resolved, setResolved] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setTerminalFailure(null);
    setResolved(false);
    if (!enabled) return;
    void loadOrgRoster(orgId, getTeamJwt)
      .then((next) => {
        if (cancelled) return;
        setMembers(next);
        setResolved(true);
      })
      .catch((error) => {
        console.error('[ProjectTrackerSession] Unable to resolve the tracker identity', error);
        if (!cancelled) {
          setFailed(true);
          setTerminalFailure(
            error instanceof TeamSessionError && error.kind !== 'service-unavailable'
              ? error.kind
              : null,
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [getTeamJwt, orgId, enabled]);
  return { members, failed, resolved, terminalFailure };
}

export interface ProjectTrackerSessionValue {
  members: readonly TeamMember[];
  /** The roster request failed; nobody can say who this reader is. */
  rosterFailed: boolean;
  /** The roster answered. With `identity` null, that answer was "not a member". */
  rosterResolved: boolean;
  identity: TrackerIdentity | null;
  presenceIdentity: { displayName: string; avatarUrl: string | null };
  presenceName: string;
  /** Null until the identity resolves, and whenever there is no project. */
  session: BrowserTrackerSession | null;
  /**
   * Live tracker references over the same room, for document bodies. Opening a
   * reference navigates to the item's typed page in Pages.
   */
  references: TrackerReferenceResolver | null;
}

const ProjectTrackerSessionContext = createContext<ProjectTrackerSessionValue | null>(null);
const ActivateProjectTrackerSessionContext = createContext<(() => void) | null>(null);

function useActivateProjectTrackerSession() {
  const activate = useContext(ActivateProjectTrackerSessionContext);
  useEffect(() => { activate?.(); }, [activate]);
}

export function useProjectTrackerSession(): ProjectTrackerSessionValue {
  useActivateProjectTrackerSession();
  const value = useContext(ProjectTrackerSessionContext);
  if (!value) throw new Error('Project tracker surfaces must render inside ProjectTrackerSessionProvider');
  return value;
}

/** For surfaces that also render outside an organization route (harnesses). */
export function useOptionalProjectTrackerSession(): ProjectTrackerSessionValue | null {
  useActivateProjectTrackerSession();
  return useContext(ProjectTrackerSessionContext);
}

/**
 * A fixed session with no roster and no room: the read-only public wiki, whose
 * items arrive as one projection. Surfaces that read the session (schema,
 * members for names) work unchanged; nothing here can write.
 */
export function StaticProjectTrackerSessionProvider({ value, children }: { value: ProjectTrackerSessionValue; children: ReactNode }) {
  return <ProjectTrackerSessionContext.Provider value={value}>{children}</ProjectTrackerSessionContext.Provider>;
}

/**
 * One tracker room per selected project, opened on the first consumer's demand.
 *
 * Mounted at the organization route rather than inside Trackers so that Docs --
 * the wiki, and live tracker references in ordinary documents -- reads the same
 * data source instead of opening a second socket and a second IndexedDB engine
 * over the same room. Switching modes therefore keeps the room connected;
 * an organization route with no tracker consumer starts no roster or room work.
 *
 * Revocation is handled here for the same reason: a roster that says this
 * session is not a member purges the org's local tracker data whichever mode
 * the reader happens to be in.
 */
export function ProjectTrackerSessionProvider({
  context,
  children,
  shutdownSignal,
}: {
  context: OrganizationRouteContext;
  children: ReactNode;
  shutdownSignal?: AbortSignal;
}) {
  const { teamSession, getTeamJwt, project, team } = context;
  const scope = JSON.stringify([teamSession.orgId, teamSession.memberId, project?.teamProjectId]);
  const [activatedScope, setActivatedScope] = useState<string | null>(null);
  // Latch demand for this scope: switching Docs/Wiki/Trackers keeps one room,
  // while visiting admin first (or selecting a different project there) is idle.
  const activate = useCallback(() => {
    if (!shutdownSignal?.aborted) setActivatedScope(scope);
  }, [scope, shutdownSignal]);
  const enabled = Boolean(project) && activatedScope === scope && !shutdownSignal?.aborted;
  const { members, failed, resolved, terminalFailure } = useTrackerRoster(teamSession.orgId, getTeamJwt, enabled);
  // The in-memory roster is only a paint cache. It must not authorize opening
  // a room: a member removed since the last visit can still be present there.
  const identity = useMemo<TrackerIdentity | null>(
    () => (resolved ? trackerIdentityForMember(members, teamSession.memberId) : null),
    [members, resolved, teamSession.memberId],
  );
  const purgeDeniedOrganization = enabled && (terminalFailure !== null || (resolved && !identity));
  useEffect(() => {
    if (!purgeDeniedOrganization) return;
    const purge = terminalFailure === 'session-expired' || terminalFailure === 'malformed-token'
      ? purgeAllBrowserTrackerData()
      : purgeBrowserTrackerOrganization(teamSession.orgId);
    void purge
      .catch((error: unknown) => console.error('[ProjectTrackerSession] Unable to purge revoked tracker data', error));
  }, [teamSession.orgId, purgeDeniedOrganization, terminalFailure]);

  const presenceIdentity = useMemo(
    () => trackerPresenceIdentity(members, teamSession.memberId),
    [members, teamSession.memberId],
  );
  const self = members.find((member) => member.memberId === teamSession.memberId);
  const presenceName = self ? memberDisplayName(self) : teamSession.memberId;

  const liveSession = useBrowserTrackerSession({
    orgId: teamSession.orgId,
    teamProjectId: project?.teamProjectId ?? '',
    teamMemberId: teamSession.memberId,
    serverUrl: consoleConfig.collabWebSocketBaseUrl,
    // No project, no room: a null identity is how the session hook waits.
    identity: enabled ? identity : null,
    shutdownSignal,
    presenceIdentity,
    getTeamJwt: () => getTeamJwt(),
    reportError: (error, where) => console.error(`[ProjectTrackerSession] ${where}`, error),
  });

  // Consumers key on the room, so the value is rebuilt only when it changes.
  const liveDataSource = liveSession?.dataSource ?? null;
  const liveSchema = liveSession?.schema ?? null;
  const liveDefineType = liveSession?.defineType;
  const session = useMemo<BrowserTrackerSession | null>(
    () => (liveDataSource && liveSchema ? { dataSource: liveDataSource, schema: liveSchema, defineType: liveDefineType } : null),
    [liveDataSource, liveSchema, liveDefineType],
  );

  // `navigate` changes identity on every location change; the resolver is
  // built once per room, so it reads the latest through a ref.
  const navigate = useNavigate();
  const openItemRef = useRef<(itemId: string) => void>(() => undefined);
  const projectRouteId = project ? getProjectRouteId(project) : null;
  openItemRef.current = (itemId: string) => {
    if (projectRouteId) navigate(orgPageItemPath(team, projectRouteId, itemId));
  };
  // Who a state change from a reference card is recorded as. Read through a
  // ref for the same reason: the roster can answer after the resolver exists.
  const actorRef = useRef<{ email: string; name?: string } | null>(null);
  actorRef.current = self?.email ? { email: self.email, name: presenceName } : null;
  // Names for user fields (owner, decidedBy) from the org member directory;
  // null lets the resolver fall back to names recorded on items.
  const namesRef = useRef<ReadonlyMap<string, string>>(new Map());
  namesRef.current = useMemo(
    () => new Map(members.filter((member) => member.email).map((member) => [member.email.toLowerCase(), memberDisplayName(member)])),
    [members],
  );

  // Built in an effect rather than a memo so StrictMode's simulated unmount
  // disposes one resolver and the remount builds a fresh one. Held together
  // with the data source it reads, and exposed only while that is still the
  // session's source: the render that switches project or member must not hand
  // out the previous scope's items for the frame before this effect runs.
  const dataSource = liveDataSource;
  const [built, setBuilt] = useState<{ dataSource: object; resolver: TrackerReferenceResolver } | null>(null);
  useEffect(() => {
    if (!dataSource) return;
    const resolver = createTrackerReferenceResolver(dataSource, {
      onOpenItem: (itemId) => openItemRef.current(itemId),
      currentActor: () => actorRef.current,
      personName: (email: string) => namesRef.current.get(email.toLowerCase()) ?? null,
    });
    setBuilt({ dataSource, resolver });
    return () => {
      resolver.dispose();
      setBuilt((current) => (current?.resolver === resolver ? null : current));
    };
  }, [dataSource]);
  const references = built && dataSource && built.dataSource === dataSource ? built.resolver : null;

  const value = useMemo<ProjectTrackerSessionValue>(() => ({
    members,
    rosterFailed: failed,
    rosterResolved: resolved,
    identity,
    presenceIdentity,
    presenceName,
    session,
    references,
  }), [members, failed, resolved, identity, presenceIdentity, presenceName, session, references]);

  return (
    <ActivateProjectTrackerSessionContext.Provider value={activate}>
      <ProjectTrackerSessionContext.Provider value={value}>
        {children}
      </ProjectTrackerSessionContext.Provider>
    </ActivateProjectTrackerSessionContext.Provider>
  );
}
